import { useState, useEffect } from "react";
import { useAuth } from "contexts/AuthContext";
import { Settings, Globe, Tag, FileText, Check, Loader2, X } from "lucide-react";

export default function ProjectSettings({ project, onUpdated }) {
  const { api, isAdmin } = useAuth();
  const [form, setForm] = useState({
    display_name: "", website: "", description: "", website_context: "", keywords: "",
  });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (project) {
      setForm({
        display_name: project.display_name || "",
        website: project.website || "",
        description: project.description || "",
        website_context: project.website_context || "",
        keywords: (project.keywords || []).join(", "),
      });
    }
  }, [project]);

  if (!project) {
    return (
      <div className="project-settings" data-testid="project-settings">
        <div className="empty-state"><Settings size={48} /><div className="empty-text">Select a project from the sidebar to view settings</div></div>
      </div>
    );
  }

  const handleSave = async () => {
    setLoading(true); setMsg(null);
    try {
      await api.patch(`/projects/${project.id}`, {
        display_name: form.display_name,
        website: form.website,
        description: form.description,
        website_context: form.website_context,
        keywords: form.keywords.split(",").map(k => k.trim()).filter(Boolean),
      });
      setMsg({ type: "success", text: "Project settings saved!" });
      onUpdated?.();
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.detail || "Save failed" });
    } finally { setLoading(false); }
  };

  return (
    <div className="project-settings" data-testid="project-settings">
      <h2 className="section-title"><Settings size={20} color="#ff4500" /> Project Settings: {project.display_name}</h2>

      {msg && (
        <div className={`alert alert-${msg.type}`}>
          {msg.type === "success" ? <Check size={14}/> : <X size={14}/>} {msg.text}
        </div>
      )}

      <div className="settings-form">
        <div className="settings-grid">
          <div className="form-field">
            <label><Globe size={12} /> Display Name</label>
            <input className="filter-input" value={form.display_name}
              onChange={e => setForm({...form, display_name: e.target.value})}
              disabled={!isAdmin} data-testid="settings-display-name" />
          </div>
          <div className="form-field">
            <label><Globe size={12} /> Website URL</label>
            <input className="filter-input" value={form.website}
              onChange={e => setForm({...form, website: e.target.value})}
              placeholder="https://example.com" disabled={!isAdmin} data-testid="settings-website" />
          </div>
          <div className="form-field full-width">
            <label><Tag size={12} /> Keywords (comma-separated)</label>
            <input className="filter-input" value={form.keywords}
              onChange={e => setForm({...form, keywords: e.target.value})}
              placeholder="windows key, office license, cheap software"
              disabled={!isAdmin} data-testid="settings-keywords" />
          </div>
          <div className="form-field full-width">
            <label><FileText size={12} /> Description</label>
            <textarea className="filter-input settings-textarea" value={form.description}
              onChange={e => setForm({...form, description: e.target.value})}
              placeholder="Short description of the project"
              rows={2} disabled={!isAdmin} data-testid="settings-description" />
          </div>
          <div className="form-field full-width">
            <label><FileText size={12} /> Website Context (used by AI for comment generation)</label>
            <textarea className="filter-input settings-textarea" value={form.website_context}
              onChange={e => setForm({...form, website_context: e.target.value})}
              placeholder="Detailed context about your website, products, pricing, value proposition... This will be used by the AI to generate relevant comments and posts."
              rows={6} disabled={!isAdmin} data-testid="settings-context" />
          </div>
        </div>

        {isAdmin && (
          <div className="form-actions" style={{marginTop:20}}>
            <button className="btn btn-primary" onClick={handleSave} disabled={loading} data-testid="save-settings-btn">
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save Settings
            </button>
          </div>
        )}

        {!isAdmin && (
          <p style={{color:'#6b7280',fontSize:13,marginTop:16}}>Only admins can edit project settings.</p>
        )}
      </div>
    </div>
  );
}
